import { motion } from "framer-motion";
import { Eye, Send, Settings, Clock } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useState } from "react";
import { Link } from "react-router-dom";
import { EmailTabNav } from "@/components/admin/email/EmailTabNav";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { EMAIL_CATEGORY_CONFIG, EMAIL_PRIORITY_CONFIG } from "@/types/email";
import type { EmailTemplateConfig, EmailCategory, EmailPriority } from "@/types/email";

type FilterStatus = "all" | "active" | "inactive" | "planned";

export default function AdminEmailTemplates() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [categoryFilter, setCategoryFilter] = useState<EmailCategory | "all">("all");
  const [statusFilter, setStatusFilter] = useState<FilterStatus>("all");
  const [recipientFilter, setRecipientFilter] = useState<"all" | "student" | "partner">("all");
  const [toggleTarget, setToggleTarget] = useState<EmailTemplateConfig | null>(null);

  const { data: templates, isLoading } = useQuery({
    queryKey: ["email-templates-config"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("email_templates_config")
        .select("*")
        .order("is_implemented", { ascending: false })
        .order("priority", { ascending: true });
      if (error) throw error;
      // Sort by priority weight
      const priorityOrder: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 };
      return ((data ?? []) as EmailTemplateConfig[]).sort((a, b) => {
        if (a.is_implemented !== b.is_implemented) return a.is_implemented ? -1 : 1;
        return (priorityOrder[a.priority] ?? 2) - (priorityOrder[b.priority] ?? 2);
      });
    },
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ key, active, displayName }: { key: string; active: boolean; displayName: string }) => {
      const { data: { user } } = await supabase.auth.getUser();
      const { error } = await supabase
        .from("email_templates_config")
        .update({
          is_active: active,
          last_toggled_by: user?.id,
          last_toggled_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq("template_key", key);
      if (error) throw error;
      return displayName;
    },
    onSuccess: (displayName, { active }) => {
      toast({ title: `"${displayName}" ${active ? "ativado" : "desativado"}` });
      queryClient.invalidateQueries({ queryKey: ["email-templates-config"] });
      setToggleTarget(null);
    },
  });

  const filtered = (templates ?? []).filter((t) => {
    if (categoryFilter !== "all" && t.category !== categoryFilter) return false;
    if (statusFilter === "active" && (!t.is_active || !t.is_implemented)) return false;
    if (statusFilter === "inactive" && (t.is_active || !t.is_implemented)) return false;
    if (statusFilter === "planned" && t.is_implemented) return false;
    if (recipientFilter !== "all") {
      if (recipientFilter === "student" && t.recipient_type === "partner") return false;
      if (recipientFilter === "partner" && t.recipient_type === "student") return false;
    }
    return true;
  });

  const categoryCounts = (templates ?? []).reduce((acc, t) => {
    acc[t.category] = (acc[t.category] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const implementedCount = (templates ?? []).filter((t) => t.is_implemented).length;
  const plannedCount = (templates ?? []).filter((t) => !t.is_implemented).length;

  return (
    <div className="p-6 lg:p-8 max-w-7xl">
      <EmailTabNav />

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Templates de Email</h1>
          <p className="text-sm text-muted-foreground">
            {templates?.length ?? 0} templates — {implementedCount} implementados, {plannedCount} planejados
          </p>
        </div>
        <div className="flex gap-2 text-xs text-muted-foreground">
          {Object.entries(categoryCounts).map(([cat, count]) => (
            <span key={cat} className="px-2 py-1 bg-muted rounded-full">
              {EMAIL_CATEGORY_CONFIG[cat as EmailCategory]?.label} ({count})
            </span>
          ))}
        </div>
      </div>

      {/* Filters */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex flex-wrap items-center gap-3 mb-6">
        {/* Category */}
        <div className="flex gap-1.5">
          {(["all", "onboarding", "transactional", "notification", "reengagement"] as const).map((cat) => (
            <button
              key={cat}
              onClick={() => setCategoryFilter(cat)}
              className={`text-[11px] font-medium px-2.5 py-1 rounded-full transition-colors ${
                categoryFilter === cat
                  ? "bg-primary text-primary-foreground"
                  : "bg-secondary text-secondary-foreground border border-border hover:bg-muted"
              }`}
            >
              {cat === "all" ? "Todas" : EMAIL_CATEGORY_CONFIG[cat].label}
            </button>
          ))}
        </div>
        <div className="h-4 w-px bg-border" />
        {/* Status */}
        <div className="flex gap-1.5">
          {([
            { value: "all" as FilterStatus, label: "Todos" },
            { value: "active" as FilterStatus, label: "Ativos" },
            { value: "inactive" as FilterStatus, label: "Inativos" },
            { value: "planned" as FilterStatus, label: "Planejados" },
          ]).map((s) => (
            <button
              key={s.value}
              onClick={() => setStatusFilter(s.value)}
              className={`text-[11px] font-medium px-2.5 py-1 rounded-full transition-colors ${
                statusFilter === s.value
                  ? "bg-primary text-primary-foreground"
                  : "bg-secondary text-secondary-foreground border border-border hover:bg-muted"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
        <div className="h-4 w-px bg-border" />
        {/* Recipient */}
        <div className="flex gap-1.5">
          {([
            { value: "all" as const, label: "Todos" },
            { value: "student" as const, label: "Aluno" },
            { value: "partner" as const, label: "Empresa" },
          ]).map((r) => (
            <button
              key={r.value}
              onClick={() => setRecipientFilter(r.value)}
              className={`text-[11px] font-medium px-2.5 py-1 rounded-full transition-colors ${
                recipientFilter === r.value
                  ? "bg-primary text-primary-foreground"
                  : "bg-secondary text-secondary-foreground border border-border hover:bg-muted"
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </motion.div>

      {/* Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-card rounded-2xl p-5 border border-border h-52 animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {filtered.map((t, i) => (
            <motion.div
              key={t.template_key}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.03 * i }}
              className={`bg-card rounded-2xl p-5 border transition-all ${
                t.is_implemented
                  ? "border-border hover:border-primary/30 hover:shadow-sm cursor-pointer"
                  : "border-dashed border-border opacity-70"
              }`}
            >
              {/* Top row: status dot + badges + toggle */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`w-2 h-2 rounded-full ${t.is_active && t.is_implemented ? "bg-green-500" : "bg-gray-300"}`} />
                  <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${EMAIL_PRIORITY_CONFIG[t.priority as EmailPriority]?.color}`}>
                    {EMAIL_PRIORITY_CONFIG[t.priority as EmailPriority]?.label}
                  </span>
                  <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${EMAIL_CATEGORY_CONFIG[t.category as EmailCategory]?.color}`}>
                    {EMAIL_CATEGORY_CONFIG[t.category as EmailCategory]?.label}
                  </span>
                  <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                    {t.recipient_type === "both" ? "Aluno + Empresa" : t.recipient_type === "student" ? "Aluno" : "Empresa"}
                  </span>
                  <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                    {t.trigger_type}
                  </span>
                  {t.requires_cron && (
                    <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-muted text-muted-foreground flex items-center gap-1">
                      <Clock className="w-3 h-3" /> Cron
                    </span>
                  )}
                  {!t.is_implemented && (
                    <span className="text-[10px] font-medium px-2 py-0.5 rounded-full border border-border text-muted-foreground">
                      Planejado
                    </span>
                  )}
                </div>
                <div title={!t.is_implemented ? "Template ainda não implementado" : undefined}>
                  <Switch
                    checked={t.is_active}
                    disabled={!t.is_implemented}
                    onCheckedChange={(checked) => {
                      if (!checked) {
                        setToggleTarget(t);
                      } else {
                        toggleMutation.mutate({ key: t.template_key, active: true, displayName: t.display_name });
                      }
                    }}
                  />
                </div>
              </div>

              {/* Name + description */}
              <Link to={`/admin/emails/templates/${t.template_key}`} className="block mb-3">
                <h3 className="text-base font-semibold text-foreground hover:text-primary transition-colors">
                  {t.display_name}
                </h3>
                <p className="text-sm text-muted-foreground line-clamp-2">{t.description}</p>
              </Link>

              {/* Stats */}
              {t.is_implemented ? (
                <div className="flex gap-3 mb-4">
                  <div className="bg-muted/50 rounded-lg p-2 flex-1 text-center">
                    <p className="text-lg font-bold tabular-nums">{t.total_sent.toLocaleString("pt-BR")}</p>
                    <p className="text-[10px] uppercase text-muted-foreground">Enviados</p>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-2 flex-1 text-center">
                    <p className={`text-lg font-bold tabular-nums ${t.total_failed > 0 ? "text-red-600" : ""}`}>
                      {t.total_failed}
                    </p>
                    <p className="text-[10px] uppercase text-muted-foreground">Falhas</p>
                  </div>
                  <div className="bg-muted/50 rounded-lg p-2 flex-1 text-center">
                    <p className="text-lg font-bold tabular-nums">
                      {t.last_sent_at
                        ? new Intl.RelativeTimeFormat("pt-BR", { numeric: "auto" }).format(
                            Math.round((new Date(t.last_sent_at).getTime() - Date.now()) / 3600000),
                            "hour"
                          )
                        : "—"}
                    </p>
                    <p className="text-[10px] uppercase text-muted-foreground">Último</p>
                  </div>
                </div>
              ) : (
                <p className="text-xs text-amber-600 mb-4">Ainda não implementado</p>
              )}

              {/* Cooldown (EM09 AC6) */}
              {t.is_implemented && t.cooldown_minutes > 0 && (
                <p className="text-xs text-muted-foreground mb-2">
                  Cooldown: {t.cooldown_minutes >= 1440 ? `${Math.round(t.cooldown_minutes / 1440)}d` : t.cooldown_minutes >= 60 ? `${Math.round(t.cooldown_minutes / 60)}h` : `${t.cooldown_minutes}min`}
                </p>
              )}

              {/* Actions */}
              <div className="flex gap-2 pt-3 border-t border-border">
                <Button variant="ghost" size="sm" asChild disabled={!t.is_implemented}>
                  <Link to={`/admin/emails/templates/${t.template_key}/preview`}>
                    <Eye className="w-4 h-4 mr-1" /> Preview
                  </Link>
                </Button>
                <Button variant="ghost" size="sm" asChild>
                  <Link to={`/admin/emails/log?template=${t.template_key}`}>
                    <Send className="w-4 h-4 mr-1" /> Logs
                  </Link>
                </Button>
                <Button variant="ghost" size="sm" asChild>
                  <Link to={`/admin/emails/templates/${t.template_key}`}>
                    <Settings className="w-4 h-4 mr-1" /> Config
                  </Link>
                </Button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Toggle confirmation dialog */}
      <AlertDialog open={!!toggleTarget} onOpenChange={(open) => !open && setToggleTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Desativar template</AlertDialogTitle>
            <AlertDialogDescription>
              Nenhum email do tipo "{toggleTarget?.display_name}" será enviado enquanto estiver desativado. Tem certeza?
            </AlertDialogDescription>
            {toggleTarget?.priority === "critical" && (
              <div className="bg-red-50 border border-red-200 p-3 rounded-xl mt-2">
                <p className="text-sm text-red-800 font-medium">
                  Este é um email crítico. Desativá-lo pode impedir cadastros ou acessos.
                </p>
              </div>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => toggleTarget && toggleMutation.mutate({ key: toggleTarget.template_key, active: false, displayName: toggleTarget.display_name })}
            >
              Desativar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
